import { type OptionsType } from 'cookies-next'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import { isBrowser } from '@/utils/env'
import { type NextCookiesStore } from '@/utils/types'

export const COOKIE_KEY = '_hvsession'
export const REFRESH_TOKEN_COOKIE_KEY = '_hvrefresh'
const ACCESS_TOKEN_SESSION_FIELD = 'token'
const MAX_AGE = 60 * 60 * 24 // 24 hours

export const COOKIE_OPTIONS = {
  maxAge: MAX_AGE,
  path: '/',
  ...(process.env.NODE_ENV === 'production' && {
    secure: true,
  }),
} satisfies OptionsType

type SaveAuthTokensParams = {
  accessToken: string
  refreshToken?: string
}

export const saveAuthTokensClientSide = (params: SaveAuthTokensParams) => {
  if (!isBrowser()) throw new Error('Must be used client-side')
  const { accessToken, refreshToken } = params
  setCookie(COOKIE_KEY, serialize(accessToken), COOKIE_OPTIONS)
  if (refreshToken) {
    setCookie(REFRESH_TOKEN_COOKIE_KEY, refreshToken, COOKIE_OPTIONS)
  } else {
    // Some flows (mobile-to-web handover) grant only an access token. We must
    // delete any pre-existing refresh token cookie rather than leave it in
    // place, so a stale value from a previous identity can't later refresh the
    // session into the wrong member.
    deleteCookie(REFRESH_TOKEN_COOKIE_KEY, COOKIE_OPTIONS)
  }
}

export const resetAuthTokensClientSide = () => {
  if (!isBrowser()) throw new Error('Must be used client-side')
  deleteCookie(COOKIE_KEY, COOKIE_OPTIONS)
  deleteCookie(REFRESH_TOKEN_COOKIE_KEY, COOKIE_OPTIONS)
}

export const getRefreshTokenClientSide = () => {
  if (!isBrowser()) throw new Error('Must be used client-side')
  const cookieValue = getCookie(REFRESH_TOKEN_COOKIE_KEY)
  if (typeof cookieValue === 'string') return cookieValue
}

export const getAccessTokenClientSide = () => {
  if (!isBrowser()) throw new Error('Must be used client-side')
  const cookieValue = getCookie(COOKIE_KEY)
  if (typeof cookieValue !== 'string') return undefined
  return deserialize(cookieValue)
}

type SaveAuthTokensServerSideParams = {
  accessToken: string
  refreshToken?: string
  cookies: NextCookiesStore
}

export const saveAuthTokensServerSide = ({ accessToken, refreshToken, cookies }: SaveAuthTokensServerSideParams) => {
  cookies.set(COOKIE_KEY, serialize(accessToken), COOKIE_OPTIONS)
  if (refreshToken) {
    cookies.set(REFRESH_TOKEN_COOKIE_KEY, refreshToken, COOKIE_OPTIONS)
  } else {
    // Some flows (mobile-to-web handover) grant only an access token. We must
    // delete any pre-existing refresh token cookie rather than leave it in
    // place, so a stale value from a previous identity can't later refresh the
    // session into the wrong member.
    cookies.delete(REFRESH_TOKEN_COOKIE_KEY)
  }
}

export const resetAuthTokensServerSide = (cookies: NextCookiesStore) => {
  cookies.delete(COOKIE_KEY)
  cookies.delete(REFRESH_TOKEN_COOKIE_KEY)
}

export const getRefreshTokenServerSide = (cookies: NextCookiesStore): string | undefined => {
  return cookies.get(REFRESH_TOKEN_COOKIE_KEY)?.value
}

export const getAccessTokenServerSide = (cookies: NextCookiesStore): string | undefined => {
  const cookieValue = cookies.get(COOKIE_KEY)?.value
  return cookieValue ? deserialize(cookieValue) : undefined
}

export const serialize = (accessToken: string) => {
  return JSON.stringify({ [ACCESS_TOKEN_SESSION_FIELD]: accessToken })
}

export const deserialize = (value: string) => {
  try {
    const data = JSON.parse(value)
    const accessToken = data[ACCESS_TOKEN_SESSION_FIELD]
    return typeof accessToken === 'string' ? accessToken : undefined
  } catch {
    console.warn('Unable to deserialize session')
    return undefined
  }
}
